import { memo, useCallback, useMemo, useState } from 'react'
import { z } from 'zod'
import { type Node, type NodeProps, Position } from '@xyflow/react'
import { isEmpty } from 'radash'
import { useShallow } from 'zustand/shallow'
import { useFlowStore } from '@/stores/flow-store'
import { useDeleteNode } from '@/hooks/use-delete-node'
import CustomHandle from '@/features/flow/_builder/components/handles/custom-handler'
import {
  NodeCard,
  NodeCardContent,
  NodeCardDescription,
  NodeCardFooter,
  NodeCardHeader,
} from '@/features/flow/_builder/components/ui/node-card'
import TextMessageNodePropertyPanel from '../../sidebar/panels/node-properties/property-panels/text-message-property-panel'
import { BuilderNode, RegisterNodeMetadata } from '../../types'
import { getNodeDetail } from '../../utils'

const NODE_TYPE = BuilderNode.TEXT_MESSAGE

const TextMessageNodeDataSchema = z.object({
  message: z.string().min(1, 'Mensagem de texto é obrigatória'),
  image: z.string().nullable().optional(),
  audio: z.string().nullable().optional(),
  video: z.string().nullable().optional(),
  file: z.string().nullable().optional(),
  deletable: z.boolean().optional(),
})

export type TextMessageNodeData = z.infer<typeof TextMessageNodeDataSchema>

type TextMessageNodeProps = NodeProps<
  Node<TextMessageNodeData, typeof NODE_TYPE>
>

export function TextMessageNode({
  id,
  isConnectable,
  selected,
  data,
}: TextMessageNodeProps) {
  const meta = useMemo(() => getNodeDetail(NODE_TYPE), [])

  const [showNodePropertiesOf] = useFlowStore(
    useShallow((s) => [s.actions.sidebar.showNodePropertiesOf])
  )
  const [sourceHandleId] = useState<string>(`${id}-source`)
  const [targetHandleId] = useState<string>(`${id}-target`)

  const deleteNode = useDeleteNode()

  const handleDeleteNode = () => {
    deleteNode(id)
  }

  const handleShowNodeProperties = useCallback(() => {
    showNodePropertiesOf({ id, type: NODE_TYPE })
  }, [id, showNodePropertiesOf])

  return (
    <NodeCard
      data-selected={selected}
      onDoubleClick={handleShowNodeProperties}
      nodeId={id}
    >
      <NodeCardHeader
        icon={meta.icon}
        title={meta.title}
        handleDeleteNode={handleDeleteNode}
        handleShowNodeProperties={handleShowNodeProperties}
        gradientColor={meta.gradientColor}
      />

      <NodeCardContent>
        <div className='flex flex-col p-4'>
          <div className='text-xs font-medium text-card-foreground'>
            Message Content
          </div>

          <div className='mt-2 text-sm leading-snug line-clamp-4'>
            {isEmpty(data.message) ? (
              <span className='italic text-card-foreground'>
                No message yet...
              </span>
            ) : (
              data.message
            )}
          </div>
          {(data.image || data.audio || data.video || data.file) && (
            <div className='flex items-center gap-2 mt-3 text-xs text-card-foreground/70'>
              {data.image && (
                <span className='inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5'>
                  <span className='w-2 h-2 rounded-full bg-primary' />
                  Image attached
                </span>
              )}
              {data.audio && (
                <span className='inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5'>
                  <span className='w-2 h-2 rounded-full bg-primary' />
                  Audio attached
                </span>
              )}
              {data.video && (
                <span className='inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5'>
                  <span className='w-2 h-2 rounded-full bg-primary' />
                  Video attached
                </span>
              )}
              {data.file && (
                <span className='inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5'>
                  <span className='w-2 h-2 rounded-full bg-primary' />
                  File attached
                </span>
              )}
            </div>
          )}
        </div>

        <NodeCardDescription description='This message will be sent to user' />

        <NodeCardFooter nodeId={id} />
      </NodeCardContent>
      <CustomHandle
        type='target'
        id={targetHandleId}
        position={Position.Left}
        isConnectable={isConnectable}
      />

      <CustomHandle
        type='source'
        id={sourceHandleId}
        position={Position.Right}
        isConnectable={isConnectable}
      />
    </NodeCard>
  )
}

export const metadata: RegisterNodeMetadata<TextMessageNodeData> = {
  type: NODE_TYPE,
  node: memo(TextMessageNode),
  detail: {
    icon: 'message-circle',
    title: 'Text Message',
    description:
      'Send formatted text with variables, plus optional image, audio, video or file.',
  },
  connection: {
    inputs: 1,
    outputs: 1,
  },
  defaultData: {
    message: '',
    image: null,
    audio: null,
    video: null,
    file: null,
  },
  propertyPanel: TextMessageNodePropertyPanel,
  validationSchema: TextMessageNodeDataSchema,
}
